import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';
import api from '../services/api';

const Members = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [referralCode] = useState(user?.referralCode || 'GBCN-' + Math.random().toString(36).substr(2, 9).toUpperCase());
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Set tab based on route
  useEffect(() => {
    if (location.pathname === '/members/refer') {
      setTabValue(1);
    } else {
      setTabValue(0);
    }
  }, [location.pathname]);

  // Load members when user is authenticated
  useEffect(() => {
    const loadMembers = async () => {
      if (!isAuthenticated) return;
      try {
        setLoading(true);
        const res = await api.get('/community/members');
        setMembers(res.data?.data || []);
      } catch (error) {
        console.error('Failed to load members', error);
        toast.error('Unable to load members right now.');
      } finally {
        setLoading(false);
      }
    };

    loadMembers();
  }, [isAuthenticated]);

  const formatLocation = (loc) => {
    if (!loc) return '';
    if (typeof loc === 'string') return loc;
    const { city, state, country } = loc;
    return [city, state, country].filter(Boolean).join(', ');
  };

  const filteredMembers = members.filter((member) => {
    const q = searchQuery.toLowerCase();
    const locStr = formatLocation(member.location).toLowerCase();
    return (
      member.name?.toLowerCase().includes(q) ||
      member.email?.toLowerCase().includes(q) ||
      member.profession?.toLowerCase().includes(q) ||
      member.gotra?.toLowerCase().includes(q) ||
      locStr.includes(q)
    );
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1) {
      navigate('/members/refer', { replace: true });
    } else {
      navigate('/members', { replace: true });
    }
  };

  const handleCopyReferral = () => {
    const referralLink = `${window.location.origin}/register?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied to clipboard!');
  };

  const handleShareWhatsApp = () => {
    const referralLink = `${window.location.origin}/register?ref=${referralCode}`;
    const message = `Join GBCN Foundation! Use my referral link: ${referralLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleShareEmail = () => {
    const referralLink = `${window.location.origin}/register?ref=${referralCode}`;
    const subject = 'Join GBCN Foundation';
    const body = `I'd like to invite you to join GBCN Foundation. Use my referral link: ${referralLink}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <Box sx={{ minHeight: '80vh', py: 8, backgroundColor: '#ffffff' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          component="h1"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 2,
            color: '#f97316',
          }}
        >
          Members
        </Typography>
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            mb: 6,
            maxWidth: '800px',
            mx: 'auto',
          }}
        >
          Connect with our community members and help grow the foundation
        </Typography>

        {!isAuthenticated && (
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Card sx={{ backgroundColor: '#f0f9ff', border: '1px solid #f97316', p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#111827' }}>
                Join GBCN Foundation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Become a member to access the members directory, track your donations, and participate in exclusive events.
              </Typography>
              <Button
                component={Link}
                to="/register"
                variant="contained"
                startIcon={<PersonAddIcon />}
                sx={{
                  backgroundColor: '#f97316',
                  '&:hover': { backgroundColor: '#ea580c' },
                }}
              >
                Register Now
              </Button>
            </Card>
          </Box>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            centered
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
              },
            }}
          >
            <Tab label="Members Directory" />
            <Tab label="Refer a Member" />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Box>
            {isAuthenticated ? (
              <>
                <TextField
                  fullWidth
                  placeholder="Search members by name, location, or expertise..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ mb: 3 }}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                {loading ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading members...
                  </Typography>
                ) : filteredMembers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No members found. Try a different search term.
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {filteredMembers.map((member) => (
                      <Grid item xs={12} sm={6} md={4} key={member._id}>
                        <Card sx={{ border: '1px solid #e5e7eb' }}>
                          <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Avatar
                              src={member.profileImage}
                              alt={member.name}
                              sx={{ width: 40, height: 40 }}
                            >
                              {member.name?.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {member.name}
                              </Typography>
                              {member.location && (
                                <Typography variant="body2" color="text.secondary">
                                  {formatLocation(member.location)}
                                </Typography>
                              )}
                              {member.profession && (
                                <Typography variant="body2" color="text.secondary">
                                  {member.profession}
                                </Typography>
                              )}
                              {member.gotra && (
                                <Chip
                                  label={member.gotra}
                                  size="small"
                                  sx={{ mt: 1, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            ) : (
              <Card sx={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', p: 4, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#111827' }}>
                  Members Directory
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Please login to access the members directory and connect with other members.
                </Typography>
                <Button
                  component={Link}
                  to="/login"
                  variant="contained"
                  sx={{
                    backgroundColor: '#f97316',
                    '&:hover': { backgroundColor: '#ea580c' },
                  }}
                >
                  Login
                </Button>
              </Card>
            )}
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', p: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#111827' }}>
                Refer a Member
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Help grow the GBCN Foundation community by referring new members. When someone joins using your referral link, 
                you'll be able to track your referrals in your dashboard.
              </Typography>

              {isAuthenticated ? (
                <>
                  <Box sx={{ mb: 4, p: 3, backgroundColor: '#f0f9ff', borderRadius: 2, border: '1px solid #f97316' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#111827' }}>
                      Your Referral Code:
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 2, color: '#f97316', fontFamily: 'monospace' }}>
                      {referralCode}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#111827' }}>
                      Your Referral Link:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <TextField
                        fullWidth
                        value={`${window.location.origin}/register?ref=${referralCode}`}
                        size="small"
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={handleCopyReferral}
                        startIcon={<ContentCopyIcon />}
                        sx={{ borderColor: '#f97316', color: '#f97316' }}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#111827' }}>
                    Share via:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={<ShareIcon />}
                      onClick={handleShareWhatsApp}
                      sx={{
                        backgroundColor: '#25D366',
                        '&:hover': { backgroundColor: '#128C7E' },
                      }}
                    >
                      WhatsApp
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<ShareIcon />}
                      onClick={handleShareEmail}
                      sx={{
                        backgroundColor: '#f97316',
                        '&:hover': { backgroundColor: '#ea580c' },
                      }}
                    >
                      Email
                    </Button>
                  </Box>
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Please login or register to get your unique referral code and start referring members.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                      component={Link}
                      to="/login"
                      variant="outlined"
                      sx={{ borderColor: '#f97316', color: '#f97316' }}
                    >
                      Login
                    </Button>
                    <Button
                      component={Link}
                      to="/register"
                      variant="contained"
                      sx={{
                        backgroundColor: '#f97316',
                        '&:hover': { backgroundColor: '#ea580c' },
                      }}
                    >
                      Register
                    </Button>
                  </Box>
                </Box>
              )}
            </Card>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Members;

